import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../context/AuthContext';

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const isReception = user?.role === 'reception';
  const isAdministration = user?.role === 'administration';
  const isAdmin = user?.role === 'admin';

  const canViewReports = isAdmin || isReception;
  const canViewUsers = isAdmin || isReception;
  const canViewMedicalContent = isAdmin || isAdministration; // Doctors, Nurses, Services, Insurance, Packages

  const [stats, setStats] = useState({
    totalUsers: 0,
    totalServices: 0,
    totalTeamMembers: 0,
    totalDoctors: 0,
    totalNurses: 0,
    totalReports: 0,
    totalInsurances: 0,
    totalPackages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const res = await axios.get('/api/stats/dashboard', { timeout: 5000 });
        setStats(res.data.data.stats);
        setLoading(false);
      } catch (err) {
        setError('حدث خطأ أثناء تحميل البيانات');
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const allQuickActions = [
    {
      title: t('admin.addService'),
      icon: 'fa-plus-circle',
      link: '/admin/services',
      color: 'info',
      roles: ['admin', 'administration']
    },
    {
      title: t('admin.addDoctor', 'إضافة طبيب'),
      icon: 'fa-user-md',
      link: '/admin/doctors',
      color: 'primary',
      roles: ['admin', 'administration']
    },
    {
      title: t('admin.addNurse', 'إضافة ممرض/ة'),
      icon: 'fa-user-nurse',
      link: '/admin/nurses',
      color: 'info',
      roles: ['admin', 'administration']
    },
    {
      title: t('admin.addInsurance'),
      icon: 'fa-shield-alt',
      link: '/admin/insurance',
      color: 'secondary',
      roles: ['admin', 'administration']
    },
    {
      title: t('admin.viewReports'),
      icon: 'fa-file-medical-alt',
      link: '/admin/reports',
      color: 'warning',
      roles: ['admin', 'reception']
    },
    {
      title: 'إرسال إشعار',
      icon: 'fa-bell',
      link: '/admin/notifications',
      color: 'danger',
      roles: ['admin']
    }
  ];

  const quickActions = allQuickActions.filter(action => action.roles.includes(user?.role));

  return (
    <div className="dashboard-container py-5" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <div className="container">
        { }
        <div className="row mb-5">
          <div className="col-12">
            <div className="welcome-banner p-5 rounded-3 shadow-sm text-white position-relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)' }}>
              <div className="position-relative z-1">

                <h1 className="display-4 fw-bold mb-2">{t('admin.dashboard')}</h1>
                <p className="lead opacity-75 mb-0">{t('admin.welcomeMessage', 'Welcome back to the admin control panel.')}</p>
              </div>
              <div className="position-absolute top-0 end-0 opacity-10 p-3">
                <i className="fas fa-chart-line fa-10x"></i>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger shadow-sm border-0" role="alert">
            <i className="fas fa-exclamation-circle me-2"></i> {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">{t('loading')}</p>
          </div>
        ) : (
          <>
            { }
            <div className="row g-4 mb-5">
              {canViewReports && (
                <div className="col-md-6 col-lg-4 col-xl-3">
                  <div className="card border-0 shadow-sm h-100 dashboard-card bg-white">
                    <div className="card-body p-4">
                      <div className="d-flex justify-content-between align-items-start mb-4">
                        <div>
                          <span className="text-muted text-uppercase small fw-bold">{t('admin.reports')}</span>
                          <h2 className="display-5 fw-bold mb-0 text-primary mt-2">{stats.totalReports}</h2>
                        </div>
                        <div className="icon-box bg-primary bg-opacity-10 text-primary rounded-circle p-3">
                          <i className="fas fa-file-medical fa-lg"></i>
                        </div>
                      </div>
                      <Link to="/admin/reports" className="text-decoration-none small fw-bold text-primary">
                        {t('admin.viewDetails')} <i className={`fas ${i18n.dir() === 'rtl' ? 'fa-arrow-left' : 'fa-arrow-right'} ms-1`}></i>
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {canViewMedicalContent && (
                <>
                  <div className="col-md-6 col-lg-4 col-xl-3">
                    <div className="card border-0 shadow-sm h-100 dashboard-card bg-white">
                      <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-start mb-4">
                          <div>
                            <span className="text-muted text-uppercase small fw-bold">{t('admin.doctors', 'الأطباء')}</span>
                            <h2 className="display-5 fw-bold mb-0 text-primary mt-2">{stats.totalDoctors}</h2>
                          </div>
                          <div className="icon-box bg-primary bg-opacity-10 text-primary rounded-circle p-3">
                            <i className="fas fa-user-md fa-lg"></i>
                          </div>
                        </div>
                        <Link to="/admin/doctors" className="text-decoration-none small fw-bold text-primary">
                          {t('admin.viewDetails')} <i className={`fas ${i18n.dir() === 'rtl' ? 'fa-arrow-left' : 'fa-arrow-right'} ms-1`}></i>
                        </Link>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4 col-xl-3">
                    <div className="card border-0 shadow-sm h-100 dashboard-card bg-white">
                      <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-start mb-4">
                          <div>
                            <span className="text-muted text-uppercase small fw-bold">{t('admin.nurses', 'الممرضين')}</span>
                            <h2 className="display-5 fw-bold mb-0 text-info mt-2">{stats.totalNurses}</h2>
                          </div>
                          <div className="icon-box bg-info bg-opacity-10 text-info rounded-circle p-3">
                            <i className="fas fa-user-nurse fa-lg"></i>
                          </div>
                        </div>
                        <Link to="/admin/nurses" className="text-decoration-none small fw-bold text-info">
                          {t('admin.viewDetails')} <i className={`fas ${i18n.dir() === 'rtl' ? 'fa-arrow-left' : 'fa-arrow-right'} ms-1`}></i>
                        </Link>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {canViewUsers && (
                <div className="col-md-6 col-lg-4 col-xl-3">
                  <div className="card border-0 shadow-sm h-100 dashboard-card bg-white">
                    <div className="card-body p-4">
                      <div className="d-flex justify-content-between align-items-start mb-4">
                        <div>
                          <span className="text-muted text-uppercase small fw-bold">{t('admin.users')}</span>
                          <h2 className="display-5 fw-bold mb-0 text-success mt-2">{stats.totalUsers}</h2>
                        </div>
                        <div className="icon-box bg-success bg-opacity-10 text-success rounded-circle p-3">
                          <i className="fas fa-users fa-lg"></i>
                        </div>
                      </div>
                      <Link to="/admin/users" className="text-decoration-none small fw-bold text-success">
                        {t('admin.viewDetails')} <i className={`fas ${i18n.dir() === 'rtl' ? 'fa-arrow-left' : 'fa-arrow-right'} ms-1`}></i>
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {canViewMedicalContent && (
                <>
                  <div className="col-md-6 col-lg-4 col-xl-3">
                    <div className="card border-0 shadow-sm h-100 dashboard-card bg-white">
                      <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-start mb-4">
                          <div>
                            <span className="text-muted text-uppercase small fw-bold">{t('admin.services')}</span>
                            <h2 className="display-5 fw-bold mb-0 text-info mt-2">{stats.totalServices}</h2>
                          </div>
                          <div className="icon-box bg-info bg-opacity-10 text-info rounded-circle p-3">
                            <i className="fas fa-stethoscope fa-lg"></i>
                          </div>
                        </div>
                        <Link to="/admin/services" className="text-decoration-none small fw-bold text-info">
                          {t('admin.viewDetails')} <i className={`fas ${i18n.dir() === 'rtl' ? 'fa-arrow-left' : 'fa-arrow-right'} ms-1`}></i>
                        </Link>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4 col-xl-3">
                    <div className="card border-0 shadow-sm h-100 dashboard-card bg-white">
                      <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-start mb-4">
                          <div>
                            <span className="text-muted text-uppercase small fw-bold">{t('admin.insurance')}</span>
                            <h2 className="display-5 fw-bold mb-0 text-secondary mt-2">{stats.totalInsurances}</h2>
                          </div>
                          <div className="icon-box bg-secondary bg-opacity-10 text-secondary rounded-circle p-3">
                            <i className="fas fa-shield-alt fa-lg"></i>
                          </div>
                        </div>
                        <Link to="/admin/insurance" className="text-decoration-none small fw-bold text-secondary">
                          {t('admin.viewDetails')} <i className={`fas ${i18n.dir() === 'rtl' ? 'fa-arrow-left' : 'fa-arrow-right'} ms-1`}></i>
                        </Link>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-4 col-xl-3">
                    <div className="card border-0 shadow-sm h-100 dashboard-card bg-white">
                      <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-start mb-4">
                          <div>
                            <span className="text-muted text-uppercase small fw-bold">{t('admin.packages')}</span>
                            <h2 className="display-5 fw-bold mb-0 text-danger mt-2">{stats.totalPackages}</h2>
                          </div>
                          <div className="icon-box bg-danger bg-opacity-10 text-danger rounded-circle p-3">
                            <i className="fas fa-box-open fa-lg"></i>
                          </div>
                        </div>
                        <Link to="/admin/packages" className="text-decoration-none small fw-bold text-danger">
                          {t('admin.viewDetails')} <i className={`fas ${i18n.dir() === 'rtl' ? 'fa-arrow-left' : 'fa-arrow-right'} ms-1`}></i>
                        </Link>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="row g-4">
              <div className="col-lg-8">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white border-0 py-3">
                    <h5 className="mb-0 fw-bold text-primary"><i className="fas fa-bolt me-2"></i>{t('admin.quickActions', 'Quick Actions')}</h5>
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      {quickActions.map((action, index) => (
                        <div key={index} className="col-md-6">
                          <Link to={action.link} className={`btn btn-outline-${action.color} w-100 p-3 text-start d-flex align-items-center hover-shadow transition-all`}>
                            <div className={`rounded-circle bg-${action.color} text-white p-2 me-3 d-flex align-items-center justify-content-center`} style={{ width: '40px', height: '40px' }}>
                              <i className={`fas ${action.icon}`}></i>
                            </div>
                            <span className="fw-bold">{action.title}</span>
                          </Link>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-lg-4">
                {isAdmin && (
                  <div className="card border-0 shadow-sm h-100 bg-gradient-primary text-white overflow-hidden position-relative">
                    <div className="position-absolute top-0 end-0 opacity-10 p-3">
                      <i className="fas fa-star fa-8x"></i>
                    </div>
                    <div className="card-body p-4 position-relative z-1 d-flex flex-column justify-content-center text-center">
                      <div className="mb-3">
                        <i className="fas fa-star fa-3x mb-3"></i>
                        <h3 className="fw-bold">{t('admin.evaluations')}</h3>
                        <p className="opacity-75">{t('admin.managePatientFeedback', 'View and manage patient feedback and ratings.')}</p>
                      </div>
                      <Link to="/admin/evaluations" className="btn btn-light text-primary fw-bold w-100 py-3 rounded-pill shadow-sm">
                        {t('admin.viewEvaluations', 'View Evaluations')}
                      </Link>
                    </div>
                  </div>
                )}
              </div>

            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;

